import React, { useCallback, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';

import { UnifiedFilterBar, FilterField } from '../../shared/UnifiedFilterBar';
import {
    FSR_CATEGORIES,
    FSR_CATEGORY_LABELS,
    FieldSafetyReportCategory,
} from '../../../helpers/fieldSafetyReports';

export interface FieldSafetyReportsFilterValues {
    categories: FieldSafetyReportCategory[];
    createdBy?: string;
    fromDate?: string;
    toDate?: string;
}

interface Props {
    onFilter: (filters: FieldSafetyReportsFilterValues) => void;
}

const CATEGORY_OPTIONS = FSR_CATEGORIES.map((c) => ({
    label: FSR_CATEGORY_LABELS[c],
    value: c,
}));

const TIME_PERIOD_LABELS: Record<string, string> = {
    '7d': 'Last 7 Days',
    '14d': 'Last 14 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
};

const VALID_PERIODS = new Set(['', '7d', '14d', '30d', '90d', 'custom']);

interface PeriodState {
    period: string;
    start: Dayjs | null;
    end: Dayjs | null;
}

const EMPTY_PERIOD: PeriodState = { period: '', start: null, end: null };

function periodToRange(period: string): { fromDate: string; toDate: string } | null {
    const days = period === '7d' ? 7 : period === '14d' ? 14 : period === '30d' ? 30 : period === '90d' ? 90 : null;
    if (days === null) return null;
    return {
        fromDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
        toDate: dayjs().format('YYYY-MM-DD'),
    };
}

function resolveDateRange(p: PeriodState): { fromDate: string; toDate: string } | null {
    if (p.period !== 'custom') return periodToRange(p.period);
    if (!p.start || !p.end || !p.start.isValid() || !p.end.isValid()) return null;
    return {
        fromDate: p.start.format('YYYY-MM-DD'),
        toDate: p.end.format('YYYY-MM-DD'),
    };
}

type Values = { categories: FieldSafetyReportCategory[]; createdBy: string };
const EMPTY_VALUES: Values = { categories: [], createdBy: '' };

export const FieldSafetyReportsFilters: React.FC<Props> = ({ onFilter }) => {
    const [values, setValues] = useState<Values>(EMPTY_VALUES);
    const [applied, setApplied] = useState<Values>(EMPTY_VALUES);
    const [periodState, setPeriodState] = useState<PeriodState>(EMPTY_PERIOD);
    const [appliedPeriod, setAppliedPeriod] = useState<PeriodState>(EMPTY_PERIOD);

    const primaryFilters: FilterField[] = useMemo(
        () => [
            {
                key: 'categories',
                label: 'Category',
                type: 'multiselect',
                options: CATEGORY_OPTIONS,
            },
            {
                key: 'createdBy',
                label: 'Created by',
                type: 'text',
                placeholder: 'Search by name...',
            },
        ],
        []
    );

    const handleChange = useCallback((key: string, value: any) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const setTimePeriod = useCallback((next: string) => {
        if (!VALID_PERIODS.has(next)) return;
        setPeriodState((prev) => {
            if (next !== 'custom') return { period: next, start: null, end: null };
            return { ...prev, period: next };
        });
    }, []);

    const setCustomStartDate = useCallback((d: Dayjs | null) => {
        setPeriodState((prev) => ({ ...prev, start: d }));
    }, []);
    const setCustomEndDate = useCallback((d: Dayjs | null) => {
        setPeriodState((prev) => ({ ...prev, end: d }));
    }, []);

    const dispatch = useCallback(
        (next: Values, nextPeriod: PeriodState) => {
            const range = resolveDateRange(nextPeriod);
            const createdBy = next.createdBy.trim();
            onFilter({
                categories: next.categories,
                ...(createdBy ? { createdBy } : {}),
                ...(range ?? {}),
            });
        },
        [onFilter]
    );

    const handleApply = useCallback(() => {
        if (periodState.period === 'custom' && (!periodState.start || !periodState.end)) {
            toast('Pick a start and end date for the custom range.', { type: 'warning', autoClose: 2500 });
            return;
        }
        setApplied(values);
        setAppliedPeriod(periodState);
        dispatch(values, periodState);
    }, [values, periodState, dispatch]);

    const handleClear = useCallback(() => {
        setValues(EMPTY_VALUES);
        setApplied(EMPTY_VALUES);
        setPeriodState(EMPTY_PERIOD);
        setAppliedPeriod(EMPTY_PERIOD);
        dispatch(EMPTY_VALUES, EMPTY_PERIOD);
    }, [dispatch]);

    const removeAppliedValues = useCallback(
        (next: Values) => {
            setValues(next);
            setApplied(next);
            dispatch(next, appliedPeriod);
        },
        [appliedPeriod, dispatch]
    );

    const removeAppliedPeriod = useCallback(() => {
        setPeriodState(EMPTY_PERIOD);
        setAppliedPeriod(EMPTY_PERIOD);
        dispatch(applied, EMPTY_PERIOD);
    }, [applied, dispatch]);

    const periodPillLabel = (p: PeriodState): string | null => {
        if (!p.period) return null;
        if (p.period === 'custom') {
            if (!p.start || !p.end) return null;
            const sameYear = p.start.year() === p.end.year();
            const startFmt = sameYear ? p.start.format('MMM D') : p.start.format('MMM D, YYYY');
            return `${startFmt} – ${p.end.format('MMM D, YYYY')}`;
        }
        return TIME_PERIOD_LABELS[p.period] ?? p.period;
    };

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        for (const cat of applied.categories) {
            pills.push({
                key: `cat:${cat}`,
                label: `Category: ${FSR_CATEGORY_LABELS[cat]}`,
                onRemove: () =>
                    removeAppliedValues({
                        ...applied,
                        categories: applied.categories.filter((c) => c !== cat),
                    }),
            });
        }
        if (applied.createdBy.trim()) {
            pills.push({
                key: 'createdBy',
                label: `Created by: ${applied.createdBy.trim()}`,
                onRemove: () => removeAppliedValues({ ...applied, createdBy: '' }),
            });
        }
        const pLabel = periodPillLabel(appliedPeriod);
        if (pLabel) {
            pills.push({
                key: 'time-period',
                label: `Time: ${pLabel}`,
                onRemove: () => removeAppliedPeriod(),
            });
        }
        return pills;
    }, [applied, appliedPeriod, removeAppliedValues, removeAppliedPeriod]);

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            values={values}
            appliedFilters={appliedFilters}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            showTimePeriod
            timePeriod={periodState.period}
            onTimePeriodChange={setTimePeriod}
            customStartDate={periodState.start}
            customEndDate={periodState.end}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
        />
    );
};

export default FieldSafetyReportsFilters;
